import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { EmptyState } from 'src/components/empty-state'
import { AssistantHint } from 'src/components/assistant-hint'
import { receiptQueries } from 'src/services/receipts/queries'
import { receiptApi } from 'src/services/receipts/api'
import { useNavigate } from 'react-router'
import { ReceiptUpload } from 'src/components/receipts/receipt-upload'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { FilterPane } from 'src/components/layout/filter-pane'
import { ContentPane } from 'src/components/layout/content-pane'
import { useState, useCallback, useEffect } from 'react'
import { Receipt, LinkedReceipt } from 'src/services/receipts'
import { InfoHint } from 'src/components/info-hint'
import { Button } from 'src/components/button'
import { clsx } from 'clsx'
import { PdfPreview } from 'src/components/pdf-preview'
import { ReceiptListPageActionWrapper } from 'src/components/receipts/receipt-list-page-action-wrapper'
import {
  LinkIcon,
  LinkSlashIcon,
  RectangleGroupIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Gate } from 'src/components/gate'
import { Modal } from 'src/components/modal'
import { OrgPicker } from 'src/components/org-picker'
import { useEligibleBillingOrgs, RECEIPT_BILLING } from 'src/hooks/use-eligible-billing-orgs'
import { useToast } from 'src/components/toast'
import { useTranslation } from 'react-i18next'
import { FiFileText } from 'react-icons/fi'
import { useAsyncJobStore } from 'src/stores/async-jobs'
import { taskTypeEnum, buildOptimisticJob } from 'src/services/async-jobs/models'
import { usePagination } from 'src/hooks/use-pagination'
import { PaginationComponent } from 'src/components/pagination'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { useViewMode } from 'src/hooks/use-view-mode'
import { useDebouncedValue } from 'src/hooks/use-debounced-value'
import { Input } from 'src/components/input'

const getFileExtension = (url: string): string => {
  try {
    const urlWithoutQuery = url.split('?')[0]
    const filename = urlWithoutQuery.split('/').pop() || ''
    const extension = filename.split('.').pop() || ''
    return extension.toLowerCase()
  } catch (error) {
    console.error('Error extracting file extension:', error)
    return ''
  }
}

export const ReceiptsPage = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const restoreActive = useAsyncJobStore((s) => s.actions.restoreActive)
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [viewMode, setViewMode] = useViewMode('receipts', 'card')
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | LinkedReceipt | null>(null)
  const [selectedForLinking, setSelectedForLinking] = useState<Set<string>>(new Set())
  const [showMergeConfirm, setShowMergeConfirm] = useState(false)
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [batchOrgId, setBatchOrgId] = useState<string>('')
  // Orgs eligible to pay for the batch (can create expenses + have credit).
  const { eligibleMemberships, canGenerate } = useEligibleBillingOrgs(RECEIPT_BILLING)
  const batchOrgName = eligibleMemberships.find((m) => m.organizationId === batchOrgId)
    ?.organizationName

  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage, resetPagination } =
    usePagination()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const receiptFilters = {
    expenseGroup__isnull: true,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  }

  useEffect(() => {
    resetPagination()
  }, [receiptFilters.expenseGroup__isnull, debouncedSearch, resetPagination])

  const { data, isLoading } = useQuery(
    receiptQueries.list({
      filters: receiptFilters,
      pagination,
      paginationCallback,
    }),
  )

  const { mutate: linkReceipts, isPending: isLinking } = useMutation({
    mutationFn: async (receiptIds: string[]) => {
      // Link all selected receipts to the first one
      const [primaryId, ...otherIds] = receiptIds
      await Promise.all(
        otherIds.map((targetId) =>
          receiptApi.csc.linkReceipt({ receiptId: primaryId, targetReceiptId: targetId }),
        ),
      )
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: receiptQueries.all() })
      setSelectedForLinking(new Set())
    },
  })

  const { mutate: unlinkReceipt, isPending: isUnlinking } = useMutation({
    mutationFn: ({ receiptId, targetReceiptId }: { receiptId: string; targetReceiptId: string }) =>
      receiptApi.csc.unlinkReceipt({ receiptId, targetReceiptId }),
    onSuccess: async (_, variables) => {
      await queryClient.refetchQueries({ queryKey: receiptQueries.all() })
      // Update selectedReceipt to remove the unlinked receipt from linkedReceiptsRef
      if (selectedReceipt && selectedReceipt.id === variables.receiptId) {
        setSelectedReceipt((prev) => {
          if (!prev || !('linkedReceiptsRef' in prev)) return prev
          return {
            ...prev,
            linkedReceiptsRef: prev.linkedReceiptsRef?.filter(
              (linked: LinkedReceipt) => linked.id !== variables.targetReceiptId,
            ),
          }
        })
      }
    },
  })

  const { mutate: batchGenerate, isPending: isBatchGenerating } = useMutation({
    mutationFn: ({ receiptIds, organization }: { receiptIds: string[]; organization: string }) =>
      receiptApi.csc.batchGenerate({ receiptIds, organization }),
    onSuccess: (data) => {
      setSelectedForLinking(new Set())
      setShowBatchModal(false)
      // Light up each receipt's spinner immediately; the ws/poll supersedes
      // these same-id entries as the jobs finish.
      data.jobs.forEach((j) => {
        restoreActive(
          buildOptimisticJob({
            id: j.asyncJobId,
            taskType: taskTypeEnum.RECEIPT_ANALYSIS,
            entityId: j.receiptId,
            entityType: 'Receipt',
            label: t('asyncJobs.analyzingReceipt'),
          }),
        )
      })
      if (data.skipped.length > 0) {
        toast({
          description: t('receiptsPage.batchSkipped', { count: data.skipped.length }),
          type: 'warning',
        })
      }
    },
    onError: () => {
      setShowBatchModal(false)
      toast({
        description: t('receiptsPage.batchGenerateError'),
        type: 'error',
      })
    },
  })

  const handleOpenBatchModal = () => {
    // No funded org can pay — send them to top up instead of opening the modal.
    if (!canGenerate) {
      navigate('/billing')
      return
    }
    setBatchOrgId(eligibleMemberships.length === 1 ? eligibleMemberships[0].organizationId : '')
    setShowBatchModal(true)
  }

  const { mutate: mergeReceipts, isPending: isMerging } = useMutation({
    mutationFn: (receiptIds: string[]) => receiptApi.csc.mergeReceipts({ receiptIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: receiptQueries.all() })
      setSelectedForLinking(new Set())
      setShowMergeConfirm(false)
    },
    onError: () => {
      toast({
        description: t('receiptsPage.mergeError', { defaultValue: 'Failed to merge receipts.' }),
        type: 'error',
      })
      setShowMergeConfirm(false)
    },
  })

  // Sync selectedReceipt with query data when it updates, but skip while modal
  // is open to avoid unnecessary re-renders and asset re-fetches.
  useEffect(() => {
    if (showModal || !selectedReceipt || !data?.results) return
    // Check top-level receipts first
    const updatedReceipt = data.results.find((r) => r.id === selectedReceipt.id)
    if (updatedReceipt) {
      setSelectedReceipt(updatedReceipt)
      return
    }
    // Check if it's a linked receipt within another receipt
    for (const receipt of data.results) {
      const linkedReceipt = receipt.linkedReceiptsRef?.find((l) => l.id === selectedReceipt.id)
      if (linkedReceipt) {
        setSelectedReceipt(linkedReceipt)
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.results, selectedReceipt?.id, showModal])

  const handleView = (receipt: Receipt | LinkedReceipt) => {
    setSelectedReceipt(receipt)
    setShowModal(true)
  }

  const handleReceiptClick = useCallback((receipt: Receipt, event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault()
      setSelectedForLinking((prev) => {
        const next = new Set(prev)
        if (next.has(receipt.id)) {
          next.delete(receipt.id)
        } else {
          next.add(receipt.id)
        }
        return next
      })
    }
  }, [])

  const handleLinkSelected = () => {
    if (selectedForLinking.size >= 2) {
      linkReceipts(Array.from(selectedForLinking))
    }
  }

  const handleClearSelection = () => {
    setSelectedForLinking(new Set())
  }

  const handleUnlink = (sourceId: string, targetId: string) => {
    unlinkReceipt({ receiptId: sourceId, targetReceiptId: targetId })
  }

  const fileExtension = selectedReceipt?.asset ? getFileExtension(selectedReceipt.asset) : ''
  const isPdf = fileExtension === 'pdf'

  // Show all receipts - linked receipts are indicated with a badge and carousel
  const receipts = data?.results || []

  return (
    <>
      <PageHeader
        title={
          <>
            <FiFileText className="inline h-5 w-5" /> {t('receiptsPage.title')}
          </>
        }
        actions={
          <>
            <Button variant="primary" onClick={() => navigate(`/expenses`)}>
              {t('receiptsPage.expenses')}
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/expense-reports`)}>
              {t('receiptsPage.reports')}
            </Button>
          </>
        }
      >
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('receiptsPage.searchLabel')}
          placeholder={t('receiptsPage.searchPlaceholder')}
          className="min-w-[120px] rounded-full px-4"
          data-testid="receipts-search"
        />
      </PageHeader>
      <MainContent>
        <FilterPane title="">
          <Gate permission="receipts.create">
            <h2 className="mb-4 text-lg font-semibold">{t('receiptsPage.uploadNewReceipt')}</h2>
            <ReceiptUpload />
            <hr className="my-6" />
          </Gate>
        </FilterPane>
        <ContentPane>
          <ListToolbar
            filters={
              <InfoHint
                text={
                  navigator.platform.includes('Mac')
                    ? t('receiptsPage.selectHintMac')
                    : t('receiptsPage.selectHintOther')
                }
                label={t('receiptsPage.selectHintLabel')}
                data-testid="receipts-select-hint"
              />
            }
            count={data?.count}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          {isLoading && <p>{t('common.loading')}</p>}
          {selectedForLinking.size >= 2 && (
            <AssistantHint id="receipts-bulk" text={t('assistantHint.receipts')} className="my-2" />
          )}
          {!isLoading && data && receipts.length === 0 && (
            <EmptyState
              title={t('receiptsPage.noReceipts')}
              assistantHint={
                <AssistantHint id="receipts-empty" text={t('assistantHint.receipts')} />
              }
              data-testid="receipts-empty-state"
            />
          )}
          <ItemCollection viewMode={viewMode} data-testid="receipts-list">
            {receipts.map((receipt) => (
              <ReceiptListPageActionWrapper
                key={receipt.id}
                receipt={receipt}
                onView={handleView}
                onClick={handleReceiptClick}
                onUnlink={handleUnlink}
                isSelected={selectedForLinking.has(receipt.id)}
                isUnlinking={isUnlinking}
                viewMode={viewMode}
              />
            ))}
          </ItemCollection>
          <PaginationComponent
            pagination={pagination}
            isLoading={isLoading}
            onNextPage={goToNextPage}
            onPrevPage={goToPrevPage}
            onGoToPage={goToPage}
            className="mt-4"
          />
        </ContentPane>
      </MainContent>

      {/* Floating Toolbar — shown when 2+ receipts selected */}
      {selectedForLinking.size >= 2 && (
        <div className="fixed bottom-6 left-1/2 z-sticky flex -translate-x-1/2 items-center gap-3 rounded-full bg-surface px-4 py-3 shadow-lg">
          <span className="text-sm font-medium text-gray-700">
            {t('receiptsPage.selected', { count: selectedForLinking.size })}
          </span>
          <Gate permission="receipts.edit">
            <Button
              variant="primary"
              onClick={handleLinkSelected}
              disabled={isLinking || isBatchGenerating || isMerging}
              className="flex items-center gap-2"
              data-testid="link-selected-button"
              title={t('receiptsPage.linkSelected')}
              aria-label={t('receiptsPage.linkSelected')}
            >
              <LinkIcon className="h-4 w-4" />
              <span className="hidden sm:inline">
                {isLinking ? t('receiptsPage.linking') : t('receiptsPage.linkSelected')}
              </span>
            </Button>
          </Gate>
          <Gate permission="receipts.create">
            <Gate permission="receipts.delete">
              <Button
                variant="secondary"
                onClick={() => setShowMergeConfirm(true)}
                disabled={isMerging || isLinking || isBatchGenerating}
                className="flex items-center gap-2"
                data-testid="merge-selected-button"
                title={t('receiptsPage.mergeSelected', { defaultValue: 'Merge' })}
                aria-label={t('receiptsPage.mergeSelected', { defaultValue: 'Merge' })}
              >
                <RectangleGroupIcon className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {isMerging
                    ? t('receiptsPage.merging', { defaultValue: 'Merging...' })
                    : t('receiptsPage.mergeSelected', { defaultValue: 'Merge' })}
                </span>
              </Button>
            </Gate>
          </Gate>
          <Gate permission="expenses.create">
            <Button
              variant="secondary"
              onClick={handleOpenBatchModal}
              disabled={isBatchGenerating || isLinking || isMerging}
              className="flex items-center gap-2"
              data-testid="batch-generate-button"
              title={t('receiptsPage.generateAll')}
              aria-label={t('receiptsPage.generateAll')}
            >
              <SparklesIcon className="h-4 w-4" />
              <span className="hidden sm:inline">
                {isBatchGenerating
                  ? t('receiptsPage.batchGenerating')
                  : t('receiptsPage.generateAll')}
              </span>
            </Button>
          </Gate>
          <Button
            variant="ghost"
            onClick={handleClearSelection}
            disabled={isLinking || isBatchGenerating || isMerging}
            data-testid="clear-selection-button"
          >
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {/* Batch generate: pick the org whose wallet pays for the whole batch */}
      <Modal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        title={t('receiptsPage.generateAll')}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              {t('billing.selectOrganization')}
            </label>
            <OrgPicker
              memberships={eligibleMemberships}
              value={batchOrgId || null}
              onChange={setBatchOrgId}
            />
            {batchOrgName && (
              <p className="mt-1 flex items-center gap-1 text-xs text-text-tertiary">
                {t('receiptsPage.batchBilledToShort', { name: batchOrgName })}
                <InfoHint
                  text={t('receiptsPage.batchBilledTo', { count: selectedForLinking.size })}
                />
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowBatchModal(false)}
              disabled={isBatchGenerating}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                batchOrgId &&
                batchGenerate({
                  receiptIds: Array.from(selectedForLinking),
                  organization: batchOrgId,
                })
              }
              disabled={isBatchGenerating || !batchOrgId}
              data-testid="batch-generate-submit-button"
            >
              {isBatchGenerating
                ? t('receiptsPage.batchGenerating')
                : t('receiptsPage.generateAll')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Merge Confirmation Dialog */}
      {showMergeConfirm && (
        <div
          className="fixed inset-0 z-modal flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setShowMergeConfirm(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-lg font-semibold text-text-primary">
              {t('receiptsPage.mergeConfirmTitle', { defaultValue: 'Merge Receipts' })}
            </h3>
            <p className="mb-6 text-sm text-text-secondary">
              {t('receiptsPage.mergeConfirmMessage', {
                count: selectedForLinking.size,
                defaultValue: `Merge ${selectedForLinking.size} receipts into one? The first selected receipt will be kept and others will be deleted. This cannot be undone.`,
              })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setShowMergeConfirm(false)}
                disabled={isMerging}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => mergeReceipts(Array.from(selectedForLinking))}
                disabled={isMerging}
                data-testid="confirm-merge-button"
              >
                {isMerging
                  ? t('receiptsPage.merging', { defaultValue: 'Merging...' })
                  : t('receiptsPage.confirmMerge', { defaultValue: 'Merge' })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showModal && selectedReceipt && (
        <div
          className="fixed inset-0 z-modal flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-auto rounded-lg bg-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              onClick={() => setShowModal(false)}
              variant="danger"
              iconOnly
              shape="circle"
              className={clsx(
                'absolute -right-2 -top-2 z-10 shadow-lg',
                'focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2',
                'transition-colors',
              )}
              tooltip={t('common.close')}
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </Button>

            <div className="max-h-full overflow-auto">
              {selectedReceipt.asset ? (
                isPdf ? (
                  <PdfPreview
                    file={selectedReceipt.asset}
                    cacheKey={selectedReceipt.id}
                    allPages
                    pageClassName="mb-4 shadow-sm"
                  />
                ) : (
                  <img
                    src={selectedReceipt.asset}
                    alt={selectedReceipt.name}
                    className="h-auto max-w-full rounded-lg"
                  />
                )
              ) : (
                <div className="flex h-48 items-center justify-center text-text-tertiary">
                  {t('receiptsPage.noAsset')}
                </div>
              )}
            </div>

            {/* Linked Receipts Section */}
            {'linkedReceiptsRef' in selectedReceipt &&
              selectedReceipt.linkedReceiptsRef &&
              selectedReceipt.linkedReceiptsRef.length > 0 && (
                <div className="mt-4 border-t pt-4" data-testid="linked-receipts-section">
                  <h3 className="mb-2 font-semibold text-gray-700">
                    {t('receiptsPage.linkedReceipts')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedReceipt.linkedReceiptsRef.map((linked) => (
                      <div
                        key={linked.id}
                        className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2"
                      >
                        <span className="text-sm">{linked.name}</span>
                        <Button
                          variant="ghost"
                          tone="error"
                          iconOnly
                          size="sm"
                          onClick={() => handleUnlink(selectedReceipt.id, linked.id)}
                          disabled={isUnlinking}
                          data-testid={`unlink-receipt-${linked.id}`}
                          tooltip={t('receipts.unlinkReceipt')}
                        >
                          <LinkSlashIcon className="h-3 w-3" aria-hidden="true" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </>
  )
}
